import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import linkstate from 'linkstate';
import Select from 'react-select';

import md5 from 'crypto-js/md5';

import { matchIdexxSpecies, matchIdexxBreed, matchIdexxGender } from '../../lib/idexxMatch';

// Render options as "Name (CODE)" so both the Norwegian name and the IDEXX
// code are searchable in the dropdown's built-in filter.
const toOption = item => (item
    ? { value: item.code, label: `${item.name} (${item.code})` }
    : null);

// Minimal style overrides — match the animal edit form's "selectAdvanced" look.
const selectStyles = {
    control: base => ({ ...base, minHeight: '38px' }),
    valueContainer: base => ({ ...base, paddingTop: '4px', paddingBottom: '4px' }),
};

@withText(props => ({
    searchPlaceholder: <Text id='dyrejournal.idexx.searchTests'>Search tests...</Text>,
    notesPlaceholder: <Text id='dyrejournal.idexx.notesPlaceholder'>Optional clinical notes (max 255 chars)</Text>,
}))
@observer
class IdexxOrderForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            error: null,
            modality: 'reference_lab',
            speciesCode: '',
            breedCode: '',
            genderCode: '',
            clinicalNotes: '',
            specimenCollectionDate: new Date().toISOString().split('T')[0],
            searchQuery: '',
            selectedTests: [],
            selectedDevice: '',
            speciesList: [],
            breedList: [],
            genderList: [],
            testList: [],
            deviceList: [],
            matchedSpecies: null,
            matchedBreed: null,
            matchedGender: null,
        };
    }

    componentDidMount() {
        this.loadReferenceData();
    }

    loadReferenceData = async () => {
        const { animal } = this.props;
        const { idexxStore } = this.props.stores;

        this.setState({ loading: true });

        try {
            // Cached loaders — only hit the API when the in-memory cache is empty.
            const [speciesList, genderList, rawTestList, deviceList, linkedResp] = await Promise.all([
                idexxStore.ensureSpecies(),
                idexxStore.ensureGenders(),
                idexxStore.ensureTests(),
                idexxStore.ensureDevices(),
                util.fetchApi('/api/integrations/idexx/linked-products'),
            ]);

            // Merge linked-product info onto each IDEXX test so the list can
            // show the price, a marker, and sort linked tests to the top.
            const linkedByCode = linkedResp?.data?.byCode || {};
            const testList = (rawTestList || []).map(t => {
                const code = t.code || t.testCode;
                const linked = linkedByCode[code] || null;
                return linked ? { ...t, linkedProduct: linked } : t;
            });

            // Hard-link lookup: resolve animalSpecies.idexxCode / animalBreed.idexxCode
            // via the backend so we don't rely on fuzzy matching when the admin has
            // already mapped them. Empty strings come back for unmapped records,
            // and the matcher falls back to aliases + name matching for those.
            let resolvedSpeciesCode = '';
            let resolvedBreedCode = '';
            if (animal && (animal.speciesId || animal.breedId)) {
                try {
                    const qs = new URLSearchParams({
                        ...(animal.speciesId ? { speciesId: String(animal.speciesId) } : {}),
                        ...(animal.breedId ? { breedId: String(animal.breedId) } : {}),
                    }).toString();
                    const resolved = await util.fetchApi(`/api/integrations/idexx/resolve-codes?${qs}`);
                    resolvedSpeciesCode = resolved?.data?.speciesCode || '';
                    resolvedBreedCode = resolved?.data?.breedCode || '';
                } catch (resolveErr) {
                    console.warn('IDEXX resolve-codes failed (falling back to fuzzy):', resolveErr?.message);
                }
            }

            // Matcher prefers explicit hard-linked codes when provided,
            // then Norwegian/English aliases (Hund → CANINE, Hann → MALE_INTACT),
            // then exact / substring / token-overlap name matching.
            const matchedSpecies = matchIdexxSpecies(animal, speciesList, resolvedSpeciesCode);
            const speciesCode = matchedSpecies?.code || '';

            // Breeds are cached per-species in the store.
            let breedList = [];
            let matchedBreed = null;
            if (speciesCode) {
                breedList = await idexxStore.ensureBreeds(speciesCode);
                matchedBreed = matchIdexxBreed(animal, breedList, resolvedBreedCode);
            }

            const matchedGender = matchIdexxGender(animal, genderList);

            // If the form opens in-house (or later switches), pre-select the
            // first ACTIVE IVLS device so the submit doesn't fail with
            // IVLS_SERIALNUMBER_REQUIRED when there's only one device.
            const defaultDevice = this.state.modality === 'in_house' && deviceList.length > 0
                ? deviceList[0].serialNumber
                : '';

            this.setState({
                loading: false,
                speciesList,
                breedList,
                genderList,
                testList,
                deviceList,
                speciesCode,
                breedCode: matchedBreed?.code || '',
                genderCode: matchedGender?.code || '',
                selectedDevice: defaultDevice,
                matchedSpecies,
                matchedBreed,
                matchedGender,
            });
        } catch (e) {
            this.setState({ loading: false, error: `Failed to load reference data: ${e.message}` });
        }
    }

    onSpeciesChange = async (e) => {
        const { idexxStore } = this.props.stores;
        const speciesCode = e.target.value;
        this.setState({ speciesCode, breedCode: '', matchedBreed: null });

        if (speciesCode) {
            const breedList = await idexxStore.ensureBreeds(speciesCode);
            this.setState({ breedList });
        } else {
            this.setState({ breedList: [] });
        }
    }

    toggleTest = (test) => {
        const { selectedTests } = this.state;
        const code = test.code || test.testCode;
        const exists = selectedTests.find(t => (t.code || t.testCode) === code);
        if (exists) {
            this.setState({ selectedTests: selectedTests.filter(t => (t.code || t.testCode) !== code) });
            return;
        }
        // No modality auto-switch — the test list is already filtered by
        // modality, so every test the user sees is compatible with their
        // current Reference Lab / In House choice.
        this.setState({ selectedTests: [...selectedTests, test] });
    }

    onModalityChange = (modality) => {
        const { deviceList, selectedDevice, selectedTests } = this.state;
        const newState = { modality };
        // Auto-pick the first ACTIVE IVLS device when switching to in-house
        // so the submit doesn't fail with IVLS_SERIALNUMBER_REQUIRED.
        if (modality === 'in_house' && !selectedDevice && deviceList && deviceList.length > 0) {
            newState.selectedDevice = deviceList[0].serialNumber;
        }
        // IDEXX rejects mixed-modality orders. Drop any previously-selected
        // tests that don't match the new modality — e.g. switching from
        // In House to Reference Lab clears IHD_* tests.
        if (Array.isArray(selectedTests) && selectedTests.length > 0) {
            const filtered = selectedTests.filter(t => {
                const isInHouse = t.inHouse === true || /^IHD[_-]/i.test(t.code || t.testCode || '');
                return modality === 'in_house' ? isInHouse : !isInHouse;
            });
            if (filtered.length !== selectedTests.length) {
                newState.selectedTests = filtered;
            }
        }
        this.setState(newState);
    }

    removeTest = (code) => {
        const { selectedTests } = this.state;
        this.setState({ selectedTests: selectedTests.filter(t => (t.code || t.testCode) !== code) });
    }

    handleSubmit = async () => {
        const { idexxStore } = this.props.stores;
        const { appState } = this.props.stores;
        const { journal, drawerLevel } = this.props;
        const {
            selectedTests, speciesCode, breedCode, genderCode,
            clinicalNotes, specimenCollectionDate, modality, selectedDevice,
        } = this.state;

        if (selectedTests.length === 0) {
            this.setState({ error: 'Please select at least one test' });
            return;
        }
        if (!speciesCode || !breedCode || !genderCode) {
            this.setState({ error: 'Please select species, breed, and gender' });
            return;
        }

        // IHD_* tests and modality=in_house both require an IVLS device.
        // Guard here so the vet gets a clear message instead of IDEXX's
        // IVLS_SERIALNUMBER_REQUIRED 400.
        const hasInHouseTest = selectedTests.some(t =>
            t.inHouse === true || /^IHD[_-]/i.test(t.code || t.testCode || ''));
        if ((modality === 'in_house' || hasInHouseTest) && !selectedDevice) {
            this.setState({
                error: 'Please select an IVLS device — in-house (IHD) tests require it.',
            });
            return;
        }

        this.setState({ loading: true, error: null });

        try {
            const testCodes = selectedTests.map(t => t.code || t.testCode);
            const ivlsSerialNumbers = modality === 'in_house' && selectedDevice ? [selectedDevice] : [];

            const response = await idexxStore.createOrder({
                tests: testCodes,
                journalId: journal.id,
                clinicalNotes,
                specimenCollectionDate,
                speciesCode,
                breedCode,
                genderCode,
                modality,
                ivlsSerialNumbers,
            });

            // Backend now saves a local record even on IDEXX error so the
            // journal keeps a trail. Hand it back to the parent so the list
            // updates regardless of outcome.
            if (this.props.onOrderCreated && response?.data?.order) {
                this.props.onOrderCreated(response.data.order);
            }

            if (response?.status !== 200) {
                this.setState({ loading: false, error: response?.message || 'Failed to create order' });
                return;
            }

            // For any submitted test that's linked to a local product, append
            // a product line to the current sale. Mirrors the pattern in
            // components/dyrejournal/addMedicine.js:197-230 — newSale.products
            // gets the new line, newSale.productAdded=true so prepareSale
            // persists it, then saveSale({fieldsToSave: ['products']}).
            try {
                const { saleStore, userStore } = this.props.stores;
                const { newSale } = saleStore;
                const user = userStore.user || {};
                const linkedLines = selectedTests
                    .filter(t => t.linkedProduct && t.linkedProduct.id)
                    .map(t => {
                        const p = t.linkedProduct;
                        const createdDate = new Date();
                        return {
                            id: p.id,
                            name: p.name || p.productNumber || (t.code || t.testCode),
                            productNumber: p.productNumber || '',
                            price: p.price || 0,
                            vat: p.vat != null ? p.vat : 25,
                            qty: 1,
                            createdDate,
                            md5: md5(JSON.stringify({
                                product: p.id,
                                idexxTest: t.code || t.testCode,
                                createdDate,
                            })).toString(),
                            saleUser: user.id,
                            saleDepartment: user.currentCustomerDepartment,
                            idexxTestCode: t.code || t.testCode,
                            idexxOrderId: response.data?.order?.id,
                        };
                    });
                if (linkedLines.length > 0 && newSale && newSale.id) {
                    const existingProducts = Array.isArray(newSale.products) ? newSale.products : [];
                    saleStore.updateObjectKeyValue('newSale', 'products', [...existingProducts, ...linkedLines]);
                    saleStore.updateObjectKeyValue('newSale', 'productAdded', true);
                    await saleStore.saveSale({
                        calledFrom: 'idexxOrderForm.addLinkedProducts',
                        fieldsToSave: ['products'],
                    });
                }
            } catch (linkErr) {
                console.warn('Failed to add linked product(s) to sale:', linkErr?.message);
            }

            // Per IDEXX: open the uiURL so the vet can confirm the
            // veterinarian name and click "Order" — this is what advances
            // the order from CREATED to SUBMITTED in IDEXX. The same URL is
            // saved on the order record (idexxUiURL) and remains accessible
            // via the "Open in VetConnect Plus" button if the popup is blocked.
            const uiUrl = response.data?.order?.idexxUiURL;
            if (uiUrl) {
                const popup = window.open(uiUrl, '_blank', 'noopener,noreferrer');
                if (!popup) {
                    console.warn('IDEXX uiURL popup was blocked. The vet must use the "Open in VetConnect Plus" button on the order to submit it.');
                }
            }

            this.setState({ loading: false });
            appState.toggleDrawerRight(false, drawerLevel);
        } catch (e) {
            this.setState({ loading: false, error: e.message || 'Failed to create order' });
        }
    }

    render() {
        const { animal, visitor } = this.props;
        const { userStore } = this.props.stores;
        const currentUser = userStore.user || {};
        const {
            loading, error, modality, speciesCode, breedCode, genderCode,
            clinicalNotes, specimenCollectionDate, searchQuery,
            selectedTests, selectedDevice,
            speciesList, breedList, genderList, testList, deviceList,
        } = this.state;

        // Filter tests by modality first, then by the search query.
        // IDEXX tests are marked `inHouse: true/false` — the IVLS Catalyst
        // catalog (IHD_*) only runs on the in-house device; everything else
        // is reference-lab. IDEXX rejects mixed-modality orders, so we show
        // only tests compatible with the current Reference Lab / In House
        // choice.
        const isInHouseTest = t => t.inHouse === true || /^IHD[_-]/i.test(t.code || t.testCode || '');
        const modalityTests = (testList || []).filter(t => (
            modality === 'in_house' ? isInHouseTest(t) : !isInHouseTest(t)
        ));
        const query = (searchQuery || '').toLowerCase();
        const searchFiltered = query
            ? modalityTests.filter(t =>
                (t.name || '').toLowerCase().includes(query)
                || (t.code || t.testCode || '').toLowerCase().includes(query))
            : modalityTests;
        // Tests linked to a local product bubble to the top (alphabetical
        // within each group). The UI also marks them and shows the price.
        const filteredTests = [...searchFiltered].sort((a, b) => {
            const aLinked = a.linkedProduct ? 1 : 0;
            const bLinked = b.linkedProduct ? 1 : 0;
            if (aLinked !== bLinked) return bLinked - aLinked;
            const an = (a.name || a.code || a.testCode || '').toLowerCase();
            const bn = (b.name || b.code || b.testCode || '').toLowerCase();
            return an < bn ? -1 : an > bn ? 1 : 0;
        });

        return (
            <div class='px-3 pb-5' style='padding-top: 48px;'>
                <h5 class='mb-3'>
                    <i class='fa-duotone fa-flask-vial me-2' />
                    <Text id='dyrejournal.idexx.orderLabTest'>Order IDEXX Lab Test</Text>
                </h5>

                {error && (
                    <div class='alert alert-danger'>
                        <i class='fa-solid fa-exclamation-triangle me-2' />
                        {error}
                    </div>
                )}

                {/* Modality Toggle */}
                <div class='mb-3'>
                    <label class='form-label fw-bold'>
                        <Text id='dyrejournal.idexx.modality'>Test Type</Text>
                    </label>
                    <div class='btn-group w-100'>
                        <button
                            type='button'
                            class={`btn ${modality === 'reference_lab' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => this.onModalityChange('reference_lab')}
                        >
                            <i class='fa-solid fa-building me-1' />
                            <Text id='dyrejournal.idexx.referenceLab'>Reference Lab</Text>
                        </button>
                        <button
                            type='button'
                            class={`btn ${modality === 'in_house' ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => this.onModalityChange('in_house')}
                        >
                            <i class='fa-solid fa-microscope me-1' />
                            <Text id='dyrejournal.idexx.inHouse'>In House</Text>
                        </button>
                    </div>
                </div>

                {/* Patient Info (read-only) */}
                <div class='card mb-3'>
                    <div class='card-header'>
                        <small class='fw-bold'>
                            <Text id='dyrejournal.idexx.patientInfo'>Patient Info</Text>
                        </small>
                    </div>
                    <div class='card-body py-2'>
                        <div class='row small'>
                            <div class='col-6'>
                                <strong>{animal?.name || '—'}</strong>
                                <div class='text-body-secondary'>
                                    {animal?.species || animal?.animalSpecies || '—'} / {animal?.breed || animal?.animalBreed || '—'}
                                </div>
                            </div>
                            <div class='col-6'>
                                <div>{visitor?.firstname || ''} {visitor?.lastname || ''}</div>
                                <div class='text-body-secondary'>
                                    <Text id='dyrejournal.idexx.vet'>Vet:</Text> {currentUser.firstname || ''} {currentUser.lastname || ''}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Species / Breed / Gender — searchable dropdowns (react-select,
                    same tech as the animal edit form's "selectAdvanced"). Users
                    can type to filter by Norwegian name OR IDEXX code. */}
                <div class='row mb-3'>
                    <div class='col-4'>
                        <label class='form-label small'>
                            <Text id='dyrejournal.idexx.species'>Species</Text> *
                        </label>
                        <Select
                            className='basic-single'
                            classNamePrefix='select'
                            options={speciesList.map(toOption)}
                            value={toOption(speciesList.find(s => s.code === speciesCode))}
                            onChange={opt => this.onSpeciesChange({ target: { value: opt ? opt.value : '' } })}
                            isSearchable={true}
                            isClearable={true}
                            placeholder='--'
                            styles={selectStyles}
                        />
                    </div>
                    <div class='col-4'>
                        <label class='form-label small'>
                            <Text id='dyrejournal.idexx.breed'>Breed</Text> *
                        </label>
                        <Select
                            className='basic-single'
                            classNamePrefix='select'
                            options={breedList.map(toOption)}
                            value={toOption(breedList.find(b => b.code === breedCode))}
                            onChange={opt => this.setState({ breedCode: opt ? opt.value : '' })}
                            isSearchable={true}
                            isClearable={true}
                            isDisabled={!speciesCode || breedList.length === 0}
                            placeholder='--'
                            styles={selectStyles}
                        />
                    </div>
                    <div class='col-4'>
                        <label class='form-label small'>
                            <Text id='dyrejournal.idexx.gender'>Gender</Text> *
                        </label>
                        <Select
                            className='basic-single'
                            classNamePrefix='select'
                            options={genderList.map(toOption)}
                            value={toOption(genderList.find(g => g.code === genderCode))}
                            onChange={opt => this.setState({ genderCode: opt ? opt.value : '' })}
                            isSearchable={true}
                            isClearable={true}
                            placeholder='--'
                            styles={selectStyles}
                        />
                    </div>
                </div>

                {/* Device selector (in-house only) */}
                {modality === 'in_house' && (
                    <div class='mb-3'>
                        <label class='form-label small'>
                            <Text id='dyrejournal.idexx.device'>IVLS Device</Text>
                        </label>
                        <Select
                            className='basic-single'
                            classNamePrefix='select'
                            options={deviceList.map(d => ({
                                value: d.serialNumber,
                                label: `${d.name || d.serialNumber} (${d.serialNumber})`,
                            }))}
                            value={(() => {
                                const d = deviceList.find(x => x.serialNumber === selectedDevice);
                                return d
                                    ? { value: d.serialNumber, label: `${d.name || d.serialNumber} (${d.serialNumber})` }
                                    : null;
                            })()}
                            onChange={opt => this.setState({ selectedDevice: opt ? opt.value : '' })}
                            isSearchable={true}
                            isClearable={true}
                            placeholder='Select device...'
                            styles={selectStyles}
                        />
                    </div>
                )}

                {/* Test Search & Selection */}
                <div class='mb-3'>
                    <label class='form-label fw-bold'>
                        <Text id='dyrejournal.idexx.selectTests'>Select Tests</Text> *
                    </label>

                    {/* Modality hint — tells the vet which IDEXX catalog is
                        currently being shown, and why IHD_ tests disappear
                        when Reference Lab is selected. */}
                    <div class='small text-body-secondary mb-2'>
                        {modality === 'in_house' ? (
                            <Text id='dyrejournal.idexx.inHouseCatalogHint'>
                                Showing IDEXX in-house catalog (IHD_* — runs on IVLS device).
                            </Text>
                        ) : (
                            <Text id='dyrejournal.idexx.referenceCatalogHint'>
                                Showing IDEXX reference-lab catalog. IHD_* in-house tests are hidden — switch to In House to see them.
                            </Text>
                        )}
                    </div>

                    {/* Selected tests badges */}
                    {selectedTests.length > 0 && (
                        <div class='mb-2'>
                            {selectedTests.map(t => (
                                <span class='badge bg-primary me-1 mb-1' key={t.code || t.testCode}>
                                    {t.name || t.code || t.testCode}
                                    <button
                                        type='button'
                                        class='btn-close btn-close-white ms-1'
                                        style='font-size: 0.5em;'
                                        onClick={() => this.removeTest(t.code || t.testCode)}
                                    />
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Search input */}
                    <input
                        type='text'
                        class='form-control form-control-sm mb-2'
                        placeholder={this.props.searchPlaceholder}
                        value={searchQuery}
                        onInput={linkstate(this, 'searchQuery')}
                    />

                    {/* Test list */}
                    <div style='max-height: 200px; overflow-y: auto; border: 1px solid var(--bs-border-color); border-radius: 10px;'>
                        {loading && (
                            <div class='p-2 text-center text-body-secondary'>
                                <Text id='dyrejournal.idexx.loadingTests'>Loading...</Text>
                            </div>
                        )}
                        {!loading && filteredTests.length === 0 && (
                            <div class='p-2 text-center text-body-secondary'>
                                <Text id='dyrejournal.idexx.noTestsFound'>No tests found</Text>
                            </div>
                        )}
                        {!loading && filteredTests.map(t => {
                            const code = t.code || t.testCode;
                            const isSelected = selectedTests.some(s => (s.code || s.testCode) === code);
                            const linked = t.linkedProduct;
                            const rowClass = `px-2 py-1 d-flex align-items-center ${isSelected ? 'bg-primary bg-opacity-10' : ''} ${linked ? 'bg-success bg-opacity-10' : ''}`;
                            return (
                                <div
                                    key={code}
                                    class={rowClass}
                                    style='cursor: pointer; border-bottom: 1px solid var(--bs-border-color);'
                                    onClick={() => this.toggleTest(t)}
                                >
                                    <input type='checkbox' class='form-check-input me-2' checked={isSelected} readOnly />
                                    <div class='flex-grow-1'>
                                        <small class='fw-bold'>
                                            {linked && <i class='fa-solid fa-tag text-success me-1' title='Linked to local product' />}
                                            {t.name || code}
                                        </small>
                                        <small class='text-body-secondary ms-2'>{code}</small>
                                    </div>
                                    {linked && (
                                        <small class='badge bg-success ms-2'>
                                            {linked.name}
                                            {linked.price != null && ` · ${linked.price} kr`}
                                        </small>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Specimen Collection Date */}
                <div class='mb-3'>
                    <label class='form-label small'>
                        <Text id='dyrejournal.idexx.collectionDate'>Specimen Collection Date</Text>
                    </label>
                    <input
                        type='date'
                        class='form-control form-control-sm'
                        value={specimenCollectionDate}
                        onInput={linkstate(this, 'specimenCollectionDate')}
                    />
                </div>

                {/* Clinical Notes */}
                <div class='mb-3'>
                    <label class='form-label small'>
                        <Text id='dyrejournal.idexx.clinicalNotes'>Clinical Notes</Text>
                    </label>
                    <textarea
                        class='form-control form-control-sm'
                        rows='2'
                        maxLength='255'
                        value={clinicalNotes}
                        onInput={linkstate(this, 'clinicalNotes')}
                        placeholder={this.props.notesPlaceholder}
                    />
                </div>

                {/* Submit */}
                <button
                    class='btn btn-success w-100'
                    onClick={this.handleSubmit}
                    disabled={loading || selectedTests.length === 0}
                >
                    {loading ? (
                        <>
                            <i class='fa-solid fa-spinner fa-spin me-2' />
                            <Text id='dyrejournal.idexx.submitting'>Submitting order...</Text>
                        </>
                    ) : (
                        <>
                            <i class='fa-solid fa-paper-plane me-2' />
                            <Text id='dyrejournal.idexx.submitOrder'>Submit Order to IDEXX</Text>
                            {selectedTests.length > 0 && ` (${selectedTests.length} test${selectedTests.length > 1 ? 's' : ''})`}
                        </>
                    )}
                </button>
            </div>
        );
    }
}

export default IdexxOrderForm;
